import Vue from 'vue'
import { FolderService, ItemService } from '@/services'
import apiFolder from './api'
import { Message } from 'element-ui'

const PAGE_SIZE = 100

const namespaced = true

const state = {
  folders: {
    root: {
      files: [],
      folderInfo: {},
      timestamp: undefined
    }
  },
  busy: false
}

const getters = {
  folderById: state => folderId => state.folders[folderId],
  folderBusy: state => state.busy
}

const actions = {
  getUserFiles ({ state, commit, dispatch }, token) {
    const timestamp = state.folders['root'].timestamp
    if (!timestamp) commit('setBusy', true)
    apiFolder
      .getUserFiles({ nextToken: token, timestamp, pageSize: PAGE_SIZE })
      .then(ItemService.decodeItems)
      .then(({ items, nextToken }) => {
        items.forEach(file => {
          dispatch('setFile', file)
          if (file.type === 5) {
            commit('prepareFolder', file.id)
            commit('setFolderInfo', file)
          }
        })

        if (nextToken && nextToken !== token) {
          dispatch('getUserFiles', nextToken)
        } else {
          commit('setTimestamp', 'root')
          commit('setBusy', false)
        }
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to fetch your files',
          showClose: true
        })
      })
  },
  getFolderFiles ({ state, commit, dispatch }, { folderId, token }) {
    commit('prepareFolder', folderId)
    const timestamp = state.folders[folderId].timestamp
    if (!timestamp) commit('setBusy', true)
    apiFolder
      .getFolderFiles(folderId, { nextToken: token, timestamp, pageSize: PAGE_SIZE })
      .then(ItemService.decodeItems)
      .then(({ items, nextToken }) => {
        items.forEach(file => {
          dispatch('setFile', file)
          if (file.type === 5) {
            commit('prepareFolder', file.id)
            commit('setFolderInfo', file)
          }
        })
        if (nextToken && nextToken !== token) {
          dispatch('getFolderFiles', { folderId, token: nextToken })
        } else {
          commit('setTimestamp', folderId)
          dispatch('getFolderInfo', folderId)
        }
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to fetch your files',
          showClose: true
        })
      })
  },
  getFolderInfo ({ state, commit }, folderId) {
    if (state.folders[folderId] && state.folders[folderId].folderInfo.name) {
      commit('setBusy', false)
    }
    apiFolder
      .getFolderInfo(folderId)
      .then(ItemService.decodeItem)
      .then((folder) => {
        commit('setFolderInfo', folder)
        commit('setBusy', false)
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to fetch folder info',
          showClose: true
        })
      })
  },
  createFolder ({ commit, dispatch }, { name, parentItemId }) {
    commit('setBusy', true)
    const payload = {
      name,
      parentItemId
    }
    FolderService
      .encodeCreateFolderRequest(payload)
      .then(apiFolder.createFolder)
      .then(FolderService.decodeCreateFolderResponse)
      .then(({ item }) => {
        dispatch('files/setFile', item, { root: true })
        commit('prepareFolder', item.id)
        commit('setFolderInfo', item)
        commit('setBusy', false)
        Message({
          type: 'success',
          message: 'New folder is: ' + item.name,
          showClose: true
        })
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, unable to create new folder',
          showClose: true
        })
      })
  },
  updateFolder ({ commit, dispatch }, { fileId, name }) {
    commit('setBusy', true)
    const payload = {
      name
    }
    FolderService
      .encodeUpdateFolderRequest(payload)
      .then(apiFolder.putUpdateFolderRequest.bind(null, fileId))
      .then(FolderService.decodeUpdateFolderResponse)
      .then(({ item }) => {
        dispatch('modal/hideAllModals', {}, { root: true })
        dispatch('files/setFile', item, { root: true })
        commit('setFolderInfo', item)
        commit('setBusy', false)
        Message({
          type: 'success',
          message: 'Folder was successfully renamed',
          showClose: true
        })
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to rename this folder',
          showClose: true
        })
      })
  },
  moveFolder ({ commit, dispatch }, { folder, parentItemId }) {
    commit('setBusy', true)
    apiFolder
      .moveFolder({ folderId: folder.id, parentItemId })
      .then(ItemService.decodeItem)
      .then((item) => {
        dispatch('modal/hideAllModals', {}, { root: true })
        dispatch('removeFile', folder)
        commit('prepareFolder', item.id)
        commit('setFolderInfo', item)
        commit('setBusy', false)
        Message({
          type: 'success',
          message: 'File was successfully moved',
          showClose: true
        })
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to move this file',
          showClose: true
        })
      })
  },
  deleteFolder ({ commit, dispatch }, folder) {
    commit('setBusy', true)
    apiFolder
      .deleteFolder(folder.id)
      .then(() => {
        dispatch('modal/hideAllModals', {}, { root: true })
        dispatch('files/removeFile', folder, { root: true })
        commit('setBusy', false)
        Message({
          type: 'success',
          message: 'Folder was successfully deleted',
          showClose: true
        })
      })
      .catch(() => {
        commit('setBusy', false)
        Message({
          type: 'error',
          message: 'Oops, we were unable to delete this folder',
          showClose: true
        })
      })
  },
  setFile ({ commit }, file) {
    commit('prepareFolder', file.parentItemId)

    if (file.status === 2) {
      commit('removeFile', file)
    } else {
      commit('setFile', file)
    }
  },
  removeFile ({ commit }, file) {
    commit('removeFile', file)
  }
}

const mutations = {
  setBusy (state, value) {
    Vue.set(state, 'busy', value)
  },
  prepareFolder (state, folderId) {
    folderId = folderId || 'root'
    if (!state.folders[folderId]) {
      let tempFolder = {
        files: [],
        folderInfo: {},
        timestamp: undefined
      }
      Vue.set(state.folders, folderId, tempFolder)
    }
  },
  setFolderInfo (state, folder) {
    Vue.set(state.folders[folder.id], 'folderInfo', folder)
  },
  setFile (state, file) {
    let parentItemId = file.parentItemId || 'root'

    if (state.folders[parentItemId]) {
      const fileIndex = state.folders[parentItemId].files.findIndex(f => {
        return f.id === file.id
      })

      if (fileIndex > -1) {
        state.folders[parentItemId].files.splice(fileIndex, 1, file)
      } else {
        state.folders[parentItemId].files.push(file)
      }
    }
  },
  removeFile (state, file) {
    let parentItemId = file.parentItemId || 'root'

    if (state.folders[parentItemId]) {
      const fileIndex = state.folders[parentItemId].files.findIndex(f => {
        return f.id === file.id
      })

      if (fileIndex > -1) {
        state.folders[parentItemId].files.splice(fileIndex, 1)
      }
    }
  },
  setTimestamp (state, folderId) {
    const timestamp = new Date().getTime()
    Vue.set(state.folders[folderId], 'timestamp', timestamp)
  }
}

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
}
